import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Inject,
  ViewContainerRef,
  Input,
  Output,
  EventEmitter,
  Injectable
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Roles } from '@eview/core/models/roles';
import { Post } from '@eview/core/domain/post/post';
import {
  User,
  Users,
  Contacts,
  ListUserOptions
} from '@eview/core/models/user';
import { selectUsers } from '@eview/core/store/selectors/users.selector';
import { selectRoles } from '@eview/core/store/selectors/roles.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Permission } from '@eview/core/auth/permission';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { ContactType } from '@eview/core/models/user';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { UserService } from '@eview/core/users/user.service';
import { ListUsers } from '@eview/core/store/actions/users.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastType } from '../../../toast/toast.component';
import { ToastService } from '../../../toast/toast.service';
import { UsersHelpers } from '@eview/core/users/users.helpers';
import { OrderSort } from '@eview/core/models/commons';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { AuthCatalogFacade } from '@eview/core/store/facades/auth-catalog.facade';
import { CreateJob, ExportUsers } from '@eview/core/store/actions/export.actions';
import { GlobalCapability } from '@eview/core/auth/form-grants';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { ExportEntityType } from '@eview/core/domain/export/export';
import { ExportService } from '@eview/core/domain/export/export.service';
import { TranslateService } from '@ngx-translate/core';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { environment } from '@eview/core';

const PAGE_SIZE = 5;
@Component({
  selector: 'eview-users-management',
  templateUrl: 'users.component.html',
  styleUrls: ['users.component.scss']
})
@Injectable()
export class UsersComponent implements OnInit, OnDestroy {
  PostHelpers = PostHelpers;
  AuthHelpers = AuthHelpers;
  AllowedPriviledge = AllowedPriviledge;
  Permission = Permission;
  UsersHelpers = UsersHelpers;

  fileToUpload: any;
  mainRole: any;
  subs: Subscription;
  userForm: UntypedFormGroup;
  contactForm: UntypedFormGroup;
  contactTypes: string[] = [];
  isAddUserClick: boolean = false;
  selectedId: number = -1;
  selectedMfaResetId: number = -1;
  selectedActiveToggleId: number = -1;
  selectedContactId: number = -1;
  readonly maxBulkSelection = 500;
  selectedIds = new Set<number>();
  showBulkConfirm = false;
  /** 'selection' = checked rows; 'all_filtered' = entire current list/filter */
  bulkMode: 'selection' | 'all_filtered' = 'selection';
  savingBulk = false;
  editId: number = -1;
  editContactId: number = -1;
  post: Post;
  listOption: ListUserOptions;
  contacts: Contacts[];
  isEditing: boolean = false;
  showContact: boolean = false;
  importForm: UntypedFormGroup;
  loggedinUser: User;
  currentPage: number = 1;
  offset: number = 0;
  limit: number = PAGE_SIZE;
  userId: number;
  users: any[] = [];
  user$: Observable<User>;
  roles$: Observable<Roles>;
  userData: any;

  public total_count: number = 0;
  format: string = environment.format.date;
  currentSearchTerm: string = '';
  searchQuery = '';
  listLoading = false;

  private readonly listRequest$ = new Subject<{
    opts: ListUserOptions;
    force?: boolean;
  }>();
  private readonly activityRequest$ = new Subject<{
    userId: number;
    offset: number;
    append: boolean;
  }>();
  private readonly destroy$ = new Subject<void>();
  activityLoading = false;
  contactsLoading = false;

  requestOptions: any;
  status = '';
  timer: any;
  regions: any[];
  currentLang: string;
  posts: any = { results: null, total_count: 0 };
  selectedUserId: number = 0;
  disableScrollDown: boolean = false;
  pageSizeList: number[] = [5, 10, 15, 20, 25];

  get pageSizeSelectOptions(): SearchableSelectOption[] {
    return this.pageSizeList.map(i => ({ value: i, label: String(i) }));
  }

  get contactTypeSelectOptions(): SearchableSelectOption[] {
    return this.contactTypes.map(type => ({
      value: type.toLowerCase(),
      label: type
    }));
  }
  @Input() isApproved: boolean;
  @Output() updateCount: EventEmitter<number>  = new EventEmitter();
  @Output() refreshInactiveCount: EventEmitter<void> = new EventEmitter();
  canResetMfa = false;

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private toast: ToastService,
    private exportService: ExportService,
    @Inject(ViewContainerRef) public viewContainerRef: ViewContainerRef,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private authCatalog: AuthCatalogFacade
  ) {
    this.subs = new Subscription();
    this.user$ = this.store
      .select(selectUser)
      .pipe(filter(user => user !== null));
    this.roles$ = this.store
      .select(selectRoles)
      .pipe(filter(roles => roles !== null));
    this.currentLang =
      this.translate.currentLang || this.translate.getDefaultLang() || 'es';
    toast.setViewContainerRef(viewContainerRef);
  }

  ngOnInit() {
    this.authCatalog.ensureRoles();

    this.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userDetails => {
        this.loggedinUser = userDetails;
      });

    this.store
      .select(selectUserPermissions)
      .pipe(takeUntil(this.destroy$))
      .subscribe(up => {
        this.canResetMfa = AuthHelpers.User.hasGlobalSync(
          up,
          GlobalCapability.UsersResetMfa
        );
        this.cdr.markForCheck();
      });

    this.listRequest$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          if (curr.force) {
            return false;
          }
          return JSON.stringify(prev.opts) === JSON.stringify(curr.opts);
        }),
        switchMap(({ opts }) => {
          this.listLoading = true;
          this.cdr.markForCheck();
          return this.userService.list(opts).pipe(
            finalize(() => {
              this.listLoading = false;
              this.cdr.markForCheck();
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.users = data.results ?? [];
        if (!this.isApproved) {
          this.users = this.users.filter(item => !item.active);
        }
        this.total_count = data.total_count;
        if (!this.isApproved) {
          this.updateCount.next(this.total_count);
        }
        this.cdr.markForCheck();
      });

    this.activityRequest$
      .pipe(
        switchMap(({ userId, offset, append }) => {
          this.activityLoading = true;
          const options: ListUserOptions = {
            offset,
            limit: this.limit,
            orderby: 'created',
            order: OrderSort.Desc
          };
          return this.userService.getUserActivity(options, userId).pipe(
            finalize(() => {
              this.activityLoading = false;
              this.cdr.markForCheck();
            }),
            map(data => ({ data, append }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ data, append }) => {
        if (append && this.posts?.results) {
          this.posts = {
            ...data,
            results: [...this.posts.results, ...data.results]
          };
        } else {
          this.posts = Object.assign({}, data);
        }
      });

    this.getRegions();
    this.initializeRequestOptions();
    this.requestUsersList();
  }

  removeByAttr(arr, attr, value) {
    var i = arr.length;
    while (i--) {
      if (
        arr[i] &&
        arr[i].hasOwnProperty(attr) &&
        (arguments.length > 2 && arr[i][attr] === value)
      ) {
        this.mainRole = arr[i];
        arr.splice(i, 1);
      }
    }
    return arr;
  }

  getRegions() {
    this.subs.add(
      this.store.select(selectMap).subscribe(map => {
        const regionsObj = map?.regions;
        if (regionsObj != null && typeof regionsObj === 'object') {
          this.regions = Object.keys(regionsObj).map(region => {
            return { value: region, name: region };
          });
        } else {
          this.regions = [];
        }
      })
    );
  }

  onApproveClick() {
    this.requestUsersList(true);
  }

  onActivityClose() {
    this.offset = 0;
    this.posts = null;
  }

  /** @deprecated Use requestUsersList() — kept for template bindings. */
  getUsersList() {
    this.requestUsersList();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.offset = 0;
    this.requestUsersList();
  }

  requestUsersList(force = false): void {
    this.listRequest$.next({ opts: this.buildListOptions(), force });
  }

  private buildListOptions(): ListUserOptions {
    const opts: ListUserOptions = {
      offset: this.offset,
      limit: this.limit,
      orderby: 'realname',
      order: OrderSort.Asc,
      active: this.isApproved ? 1 : 0,
      include_contacts: 0
    };
    if (this.currentSearchTerm) {
      opts.q = this.currentSearchTerm;
    }
    return opts;
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get selectableUsers(): any[] {
    return (this.users || []).filter(user => this.canSelectUser(user));
  }

  get allPageSelected(): boolean {
    const selectable = this.selectableUsers;
    return (
      selectable.length > 0 &&
      selectable.every(user => this.selectedIds.has(user.id))
    );
  }

  canSelectUser(user: any): boolean {
    if (!user?.id) {
      return false;
    }
    // Cannot deactivate yourself via bulk on the active tab
    if (this.isApproved && this.loggedinUser && this.loggedinUser.id === user.id) {
      return false;
    }
    return true;
  }

  /**
   * Hard-delete only when the user has no authored reports (posts.user_id).
   * Mirrors backend UserRepository::delete / list has_posts flag.
   */
  canDeleteUser(user: any): boolean {
    if (!user?.id) {
      return false;
    }
    return !user.has_posts;
  }

  isUserSelected(user: any): boolean {
    return !!(user?.id && this.selectedIds.has(user.id));
  }

  onToggleSelectUser(user: any, checked: boolean): void {
    if (!this.canSelectUser(user)) {
      return;
    }
    if (checked) {
      if (this.selectedIds.size >= this.maxBulkSelection) {
        return;
      }
      this.selectedIds.add(user.id);
    } else {
      this.selectedIds.delete(user.id);
    }
    this.showBulkConfirm = false;
  }

  onToggleSelectPage(checked: boolean): void {
    const selectable = this.selectableUsers;
    if (checked) {
      for (const user of selectable) {
        if (this.selectedIds.size >= this.maxBulkSelection) {
          break;
        }
        this.selectedIds.add(user.id);
      }
    } else {
      for (const user of selectable) {
        this.selectedIds.delete(user.id);
      }
    }
    this.showBulkConfirm = false;
  }

  onClearSelection(): void {
    this.selectedIds.clear();
    this.showBulkConfirm = false;
    this.bulkMode = 'selection';
    this.savingBulk = false;
  }

  onRequestBulkActiveToggle(): void {
    if (!this.selectedCount || this.savingBulk) {
      return;
    }
    this.bulkMode = 'selection';
    this.showBulkConfirm = true;
  }

  onRequestBulkAllFiltered(): void {
    if (!this.total_count || this.savingBulk) {
      return;
    }
    this.bulkMode = 'all_filtered';
    this.showBulkConfirm = true;
  }

  onCancelBulkConfirm(): void {
    this.showBulkConfirm = false;
    this.bulkMode = 'selection';
  }

  get bulkConfirmCount(): number {
    return this.bulkMode === 'all_filtered' ? this.total_count : this.selectedCount;
  }

  onConfirmBulkActiveToggle(): void {
    if (this.savingBulk) {
      return;
    }
    if (this.bulkMode === 'selection' && !this.selectedCount) {
      return;
    }
    if (this.bulkMode === 'all_filtered' && !this.total_count) {
      return;
    }

    const active = !this.isApproved;
    const expectedCount = this.bulkConfirmCount;
    this.savingBulk = true;

    const request$ =
      this.bulkMode === 'all_filtered'
        ? this.userService.bulkSetActive({
            all_matching: true,
            active,
            filter: {
              active: this.isApproved ? 1 : 0,
              ...(this.currentSearchTerm ? { q: this.currentSearchTerm } : {})
            }
          })
        : this.userService.bulkSetActive({
            ids: Array.from(this.selectedIds),
            active
          });

    request$.subscribe(
      response => {
        this.savingBulk = false;
        this.showBulkConfirm = false;
        this.onClearSelection();
        this.toast.show(
          ToastType.Success,
          active ? 'TOAST_BULK_ACTIVATE_USER' : 'TOAST_BULK_DEACTIVATE_USER',
          true,
          4000,
          { count: response?.updated ?? expectedCount }
        );
        this.store.dispatch(new ListUsers());
        this.requestUsersList(true);
        this.refreshInactiveCount.emit();
        this.cdr.markForCheck();
      },
      () => {
        this.savingBulk = false;
        this.toast.show(ToastType.Danger, 'TOAST_BULK_ACTIVE_ERROR');
        this.cdr.markForCheck();
      }
    );
  }

  setContactTypeValidation() {
    const type = this.contactForm.controls['type'].value;
    const contactControl = this.contactForm.controls['contact'];
    
    contactControl.clearValidators();
    contactControl.setValue('');
    
    if (!type) {
      contactControl.disable();
    } else {
      contactControl.enable();
      const noLeadingSpaces = (control) => {
        const value = control.value;
        return value && value.startsWith(' ') ? { leadingSpace: true } : null;
      };
      
      if (type === "email") {
        contactControl.setValidators([Validators.required, Validators.email, noLeadingSpaces]);
      } else if (type === "phone") {
        contactControl.setValidators([Validators.required, Validators.minLength(8), Validators.pattern(/^\+[1-9]\d{1,14}$/), noLeadingSpaces]);
      } else {
        contactControl.setValidators([Validators.required, noLeadingSpaces]);
      }
    }
    
    contactControl.updateValueAndValidity();
  }

  createContactForm() {
    this.contactTypes = Object.keys(ContactType);
    this.contactForm = this.formBuilder.group({
      type: ['', Validators.required],
      contact: [{value: '', disabled: true}, [Validators.required]]
    });
  }

  onContactClick(user: User) {
    this.userId = user.id;
    this.isAddUserClick = true;
    this.contacts = Array.isArray(user.contacts) ? [...user.contacts] : [];
    this.contactsLoading = true;
    this.cdr.markForCheck();
    // List loads with include_contacts: 0, so always fetch contacts when opening
    this.userService
      .list({
        offset: 0,
        limit: 1,
        orderby: 'id',
        order: OrderSort.Asc,
        q: user.email,
        active: user.active === false ? 0 : 1,
        include_contacts: 1
      })
      .pipe(
        finalize(() => {
          this.contactsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(data => {
        const row =
          data.results?.find(u => u.id === user.id) ?? data.results?.[0];
        if (row?.contacts) {
          user.contacts = row.contacts;
          this.contacts = row.contacts;
        } else {
          user.contacts = [];
          this.contacts = [];
        }
      });
  }

  onAddContactClick(content) {
    this.showContact = true;
    this.isEditing = false;
    this.createContactForm();
    this.modalService.open(content, { centered: true });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.unsubscribe();
  }

  onDeleteClick(index: number) {
    this.selectedMfaResetId = -1;
    this.selectedActiveToggleId = -1;
    this.selectedId = index;
  }

  onResetMfaClick(index: number) {
    if (!this.canResetMfa) {
      this.toast.show(ToastType.Warning, 'TOAST_FORBIDDEN');
      return;
    }
    this.selectedId = -1;
    this.selectedActiveToggleId = -1;
    this.selectedMfaResetId = index;
  }

  onToggleActiveClick(index: number) {
    this.selectedId = -1;
    this.selectedMfaResetId = -1;
    this.selectedActiveToggleId = index;
  }

  onCancelClick() {
    this.isEditing = false;
    this.modalService.dismissAll();
    this.userForm.reset();
  }

  onConfirmClick(userId: number) {
    if (!userId) {
      return;
    }
    this.userService.deleteUser(userId).subscribe(
      () => {
        this.selectedId = -1;
        this.toast.show(ToastType.Success, 'TOAST_DELETE_USER');
        this.store.dispatch(new ListUsers());
        this.requestUsersList(true);
        this.refreshInactiveCount.emit();
        this.cdr.markForCheck();
      },
      (err) => {
        this.selectedId = -1;
        const status = err?.status;
        const msg = String(
          err?.error?.errors?.[0]?.message ||
            err?.error?.message ||
            err?.message ||
            ''
        );
        const lower = msg.toLowerCase();
        if (
          status === 409 ||
          lower.includes('user_delete_has_posts') ||
          lower.includes('cannot be deleted due to dependencies') ||
          lower.includes('dependencies')
        ) {
          this.toast.show(ToastType.Warning, 'TOAST_DELETE_USER_HAS_POSTS');
        } else {
          this.toast.show(ToastType.Danger, 'TOAST_DELETE_USER_ERROR');
        }
        this.cdr.markForCheck();
      }
    );
  }

  onConfirmResetMfaClick(userId: number) {
    if (!userId) {
      return;
    }
    if (!this.canResetMfa) {
      this.selectedMfaResetId = -1;
      this.toast.show(ToastType.Warning, 'TOAST_FORBIDDEN');
      return;
    }
    this.userService.resetUserMfa(userId).subscribe(
      () => {
        this.selectedMfaResetId = -1;
        const user = this.users.find(u => u.id === userId);
        if (user) {
          user.two_factor_enabled = false;
        }
        this.toast.show(ToastType.Success, 'TOAST_RESET_USER_MFA');
        this.store.dispatch(new ListUsers());
        this.requestUsersList(true);
        this.cdr.markForCheck();
      },
      (err) => {
        this.selectedMfaResetId = -1;
        if (err?.status === 403) {
          this.toast.show(ToastType.Warning, 'TOAST_FORBIDDEN');
        } else {
          this.toast.show(ToastType.Danger, 'TOAST_RESET_USER_MFA_ERROR');
        }
        this.cdr.markForCheck();
      }
    );
  }

  onConfirmToggleActiveClick(userId: number, active: boolean) {
    if (!userId) {
      return;
    }
    if (!active && this.loggedinUser && this.loggedinUser.id === userId) {
      this.selectedActiveToggleId = -1;
      this.toast.show(ToastType.Danger, 'TOAST_DEACTIVATE_SELF_ERROR');
      return;
    }
    this.userService.setUserActive(userId, active).subscribe(
      () => {
        this.selectedActiveToggleId = -1;
        this.users = this.users.filter(u => u.id !== userId);
        this.total_count = Math.max(0, (this.total_count || 0) - 1);
        if (!this.isApproved) {
          this.updateCount.emit(this.total_count);
        }
        this.toast.show(
          ToastType.Success,
          active ? 'TOAST_APPROVE_USER' : 'TOAST_DEACTIVATE_USER'
        );
        this.store.dispatch(new ListUsers());
        this.requestUsersList(true);
        this.refreshInactiveCount.emit();
        this.cdr.markForCheck();
      },
      () => {
        this.toast.show(
          ToastType.Danger,
          active ? 'TOAST_ACTIVATE_USER_ERROR' : 'TOAST_DEACTIVATE_USER_ERROR'
        );
      }
    );
  }

  onAddUserClick(content) {
    this.isEditing = false;
    this.userData = [];
    this.modalService.open(content, { centered: true, backdrop: 'static' });
  }

  onEditClick(content, id) {
    this.isEditing = true;
    this.userData = this.users[id];
    if (this.userData) {
      this.editId = this.userData.id;
    }
    this.modalService.dismissAll();
    this.modalService.open(content, { centered: true, backdrop: 'static' });
  }

  onEditContactClick(content, i) {
    this.createContactForm();
    this.isEditing = true;
    const contact = this.contacts[i];

    if (contact) {
      this.editContactId = contact.id;
      this.contactForm.patchValue({
        type: contact.type,
        contact: contact.contact
      });
      // Aplicar validaciones sin limpiar el valor
      const contactControl = this.contactForm.get('contact');
      const type = contact.type;
      
      contactControl?.enable();
      contactControl?.clearValidators();
      
      const noLeadingSpaces = (control) => {
        const value = control.value;
        return value && value.startsWith(' ') ? { leadingSpace: true } : null;
      };
      
      if (type === "email") {
        contactControl?.setValidators([Validators.required, Validators.email, noLeadingSpaces]);
      } else if (type === "phone") {
        contactControl?.setValidators([Validators.required, Validators.minLength(8), Validators.pattern(/^\+[1-9]\d{1,14}$/), noLeadingSpaces]);
      } else {
        contactControl?.setValidators([Validators.required, noLeadingSpaces]);
      }
      
      contactControl?.updateValueAndValidity();
    }

    this.modalService.dismissAll();
    this.modalService.open(content, { centered: true });
  }

  onSubmitClick() {
    if (this.currentSearchTerm) {
      this.currentSearchTerm = this.searchQuery?.trim() || this.currentSearchTerm;
      this.requestUsersList(true);
    } else {
      this.requestUsersList(true);
    }
    this.modalService.dismissAll();
  }

  onContactConfirmClick(contactId: number) {
    if (contactId) {
      this.userService.deleteContact(contactId).subscribe(() => {
        this.contacts = this.contacts.filter(c => c.id !== contactId);
        // Actualizar también en la lista de usuarios
        const user = this.users.find(u => u.id === this.userId);
        if (user && user.contacts) {
          user.contacts = user.contacts.filter(c => c.id !== contactId);
        }
        this.selectedContactId = -1;
        this.toast.show(ToastType.Success, 'TOAST_CONTACT_DELETED');
      });
    }
  }

  onContactSubmitClick() {
    const reqBody: Contacts = this.contactForm.value;
    reqBody.type = reqBody.type;
    reqBody.user_id = this.userId;
    reqBody.data_source = 'web';

    if (this.isEditing && this.editContactId) {
      this.userService
        .updateContact(reqBody, this.editContactId)
        .subscribe(results => {
          const contactIndex = this.contacts.findIndex(c => c.id === this.editContactId);
          if (contactIndex !== -1) {
            this.contacts[contactIndex] = results;
          }
          this.isEditing = false;
          this.contactForm.reset();
          this.toast.show(ToastType.Success, 'TOAST_CONTACT_UPDATED');
        });
    } else {
      this.userService.addContacts(reqBody).subscribe(results => {
        this.contacts.push(results);
        this.contactForm.reset();
        this.toast.show(ToastType.Success, 'TOAST_CONTACT_SAVED');
      });
    }
    this.modalService.dismissAll();
  }

  private initializeRequestOptions() {
    this.requestOptions = {
      status: 'all',
      offset: this.offset,
      limit: this.limit,
      parent: null
    };
  }

  onExportUserList() {
    this.store.dispatch(
      new ExportUsers()
    );
  }

  onImportUserList(userImport) {
    // this.importForm.reset();
    this.importForm = this.formBuilder.group({
      file: ['', Validators.required]
    });
    this.modalService.open(userImport, { centered: true });
  }

  triggerCSVValidation(el) {
    const regex = new RegExp('(.*?).(csv)$');
    if (!regex.test(el.target.value.toLowerCase())) {
      el.target.value = '';
      this.toast.show(ToastType.Danger, 'TOAST_IMPORT_CSV');
    } else {
      this.fileToUpload = el.target.files.item(0);
    }
  }

  onSubmitImport() {
    if (this.fileToUpload === undefined) {
      this.toast.show(ToastType.Danger, 'TOAST_IMPORT_SELECT_FILE');
    } else {
      const user_id: any = this.loggedinUser.id;
      let fileData: FormData = new FormData();

      fileData.append('csv', this.fileToUpload, this.fileToUpload.name);
      fileData.append('user_id', user_id);

      this.userService.importUser(fileData).subscribe(results => {
        this.importForm.reset();
        this.modalService.dismissAll();

        this.store.dispatch(new ListUsers());
        this.toast.show(ToastType.Success, 'TOAST_IMPORT_JOB_SUCCESS');

        this.timer = setInterval(() => {
          this.checkUserList(results['id']);
        }, 10000);
      });
    }
  }

  checkUserList(id) {
    this.exportService.readJob(id).subscribe(results => {
      if (results.status == 'SUCCESS') {
        clearInterval(this.timer);
        this.getUsersList();
      }
    });
  }

  pageChanged(pageNumber) {
    this.currentPage = pageNumber;
    this.offset = pageNumber > 0 ? (pageNumber - 1) * this.limit : 0;
    this.isAddUserClick = false;
    if (this.currentSearchTerm) {
      this.performSearch(this.currentSearchTerm);
    } else {
      this.getUsersList();
    }
  }

  performSearch(term: string) {
    this.currentSearchTerm = term;
    this.requestUsersList();
  }

  getUser(item, users) {
    return PostHelpers.FormatUser(item.user_id, users);
  }

  getTypes(item) {
    if (item.type === 'create') {
      return 'created';
    } else {
      return 'updated';
    }
  }

  get contact(): any { return this.contactForm.get('contact'); }

  onContactKeyPress(event: KeyboardEvent) {
    const type = this.contactForm.get('type')?.value;
    const char = event.key;
    
    if (type === 'phone') {
      // Solo números y símbolo +
      if (!/[0-9+]/.test(char) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(char)) {
        event.preventDefault();
      }
    } else if (type === 'email') {
      // Caracteres válidos para email
      if (!/[a-zA-Z0-9@._-]/.test(char) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(char)) {
        event.preventDefault();
      }
    }
    // Twitter permite todos los caracteres
  }

  getPostName(val) {
    const postDetail = JSON.parse(val);

    return postDetail && postDetail.title !== ''
      ? '"' + postDetail.title + '"'
      : '';
  }

  getContact(item, users) {
    let data = {};

    users.forEach(user => {
      if (user.id === parseInt(item.user_id)) {
        data = user;
      }
    });

    if (data['contacts'] !== undefined && data['contacts'].length > 0) {
      return data['contacts'][0]['contact'];
    } else {
      return null;
    }
  }

  private loadActivity(append = false): void {
    this.activityRequest$.next({
      userId: this.selectedUserId,
      offset: this.offset,
      append
    });
  }

  getActivityList(): void {
    this.loadActivity(false);
  }

  onScrollDown(): void {
    this.offset += PAGE_SIZE;

    if (this.offset >= this.posts['total_count']) {
      this.disableScrollDown = true;
    }

    if (this.disableScrollDown) {
      return;
    }

    this.loadActivity(true);
  }

  onShowUserActivityClick(content, userId): void {
    this.modalService.open(content, { centered: true });
    this.selectedUserId = userId;
    this.offset = 0;
    this.disableScrollDown = false;
    this.posts = { results: null, total_count: 0 };
    this.loadActivity(false);
  }

  clearSearch() {
    this.searchQuery = '';
    this.currentSearchTerm = '';
    this.offset = 0;
    this.currentPage = 1;
    this.isAddUserClick = false;
    this.getUsersList();
  }

  onSearchQueryChange(value: string) {
    if (!(value || '').trim() && this.currentSearchTerm) {
      this.clearSearch();
    }
  }

  onSearchSubmit() {
    const term = (this.searchQuery || '').trim();
    this.offset = 0;
    this.currentPage = 1;
    this.isAddUserClick = false;
    // Keep bulk selection across searches so users can pick rows from
    // multiple result sets before deactivate / activate.
    this.showBulkConfirm = false;
    this.bulkMode = 'selection';

    if (term.length >= 2) {
      this.currentSearchTerm = term;
      this.performSearch(term);
    } else if (term.length === 0) {
      this.clearSearch();
    }
  }
}
